import { useState } from "react";
import Layout from "../../components/Layout";
import { useGetAllLaptop } from "../../hooks/query/useGetAllLaptop";

// ── Types ────────────────────────────────────────────────────────────────────
interface Laptop {
    id: number;
    model: string;
    brand: string;
    price: number;
    serialNumber: string;
    ram: number;
    storage: number;
    screenSize: number;
    gpu: string;
    cpu: string;
    batteryStatus: number;
    condition: string;
    description: string;
    imageUrl?: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
}

interface Meta {
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
}

const STATUS_OPTIONS = [
    { label: "Tất cả", value: "" },
    { label: "Còn trống", value: "available" },
    { label: "Đang cho thuê", value: "rented" },
];

// SVG laptop placeholder
const LAPTOP_PLACEHOLDER = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 220" fill="none">
  <rect width="320" height="220" fill="#f1f5f9"/>
  <rect x="60" y="40" width="200" height="130" rx="10" fill="#cbd5e1"/>
  <rect x="72" y="52" width="176" height="106" rx="4" fill="#e2e8f0"/>
  <rect x="85" y="65" width="150" height="80" rx="2" fill="#bfdbfe"/>
  <rect x="105" y="80" width="110" height="8" rx="4" fill="#93c5fd"/>
  <rect x="115" y="96" width="90" height="6" rx="3" fill="#bfdbfe"/>
  <rect x="120" y="110" width="80" height="6" rx="3" fill="#bfdbfe"/>
  <rect x="30" y="170" width="260" height="12" rx="6" fill="#94a3b8"/>
  <rect x="20" y="178" width="280" height="6" rx="3" fill="#cbd5e1"/>
  <circle cx="160" cy="173" r="4" fill="#64748b"/>
</svg>
`)}`;

// ── Sub-components ───────────────────────────────────────────────────────────
function ConditionBadge({ condition }: { condition: string }) {
    const map: Record<string, string> = {
        "Like New": "bg-emerald-100 text-emerald-700",
        "Good": "bg-blue-100 text-blue-700",
        "Fair": "bg-yellow-100 text-yellow-700",
        "Poor": "bg-red-100 text-red-700",
    };
    return (
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[condition] ?? "bg-gray-100 text-gray-600"}`}>
            {condition}
        </span>
    );
}

function CardSkeleton() {
    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
            <div className="bg-gray-200 h-48 w-full" />
            <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-8 bg-gray-200 rounded-xl w-full mt-2" />
            </div>
        </div>
    );
}

function LaptopCard({ laptop, idx, page, limit }: { laptop: Laptop; idx: number; page: number; limit: number }) {
    const [imgError, setImgError] = useState(false);

    return (
        <div className="group bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col">
            {/* Image */}
            <div className="relative bg-slate-50 h-48 flex items-center justify-center overflow-hidden">
                <img
                    src={imgError || !laptop.imageUrl ? LAPTOP_PLACEHOLDER : laptop.imageUrl}
                    onError={() => setImgError(true)}
                    alt={laptop.model}
                    className="object-contain h-full w-full p-4 group-hover:scale-105 transition-transform duration-300"
                />
                {/* Badge số thứ tự */}
                <span className="absolute top-3 left-3 bg-white/80 backdrop-blur-sm text-gray-500 text-xs font-mono px-2 py-0.5 rounded-full border border-gray-200">
                    #{(page - 1) * limit + idx + 1}
                </span>
            </div>

            {/* Content */}
            <div className="p-4 flex flex-col flex-1 gap-2">
                {/* Brand + Condition */}
                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
                        {laptop.brand}
                    </span>
                    <ConditionBadge condition={laptop.condition} />
                </div>

                {/* Model */}
                <h3 className="font-semibold text-gray-900 text-base leading-snug line-clamp-2">
                    {laptop.model}
                </h3>

                {/* Specs */}
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
                    <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                        </svg>
                        RAM: <strong className="text-gray-700">{laptop.ram}GB</strong>
                    </span>
                    <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                        </svg>
                        SSD: <strong className="text-gray-700">{laptop.storage}GB</strong>
                    </span>
                    <span className="flex items-center gap-1 col-span-2 truncate">
                        <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3l3 3m0 0l3-3m-3 3V1m0 14l-3 3m3-3l3 3m-3-3v2M3 12h18" />
                        </svg>
                        <span className="truncate">{laptop.cpu}</span>
                    </span>
                    <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <rect x="2" y="4" width="20" height="14" rx="2" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 20h8M12 18v2" />
                        </svg>
                        <strong className="text-gray-700">{laptop.screenSize}"</strong>
                    </span>
                    <span className="flex items-center gap-1">
                        🔋 <strong className="text-gray-700">{laptop.batteryStatus}%</strong>
                    </span>
                </div>

                {/* Description */}
                {laptop.description && (
                    <p className="text-xs text-gray-400 line-clamp-2 mt-1">{laptop.description}</p>
                )}

                {/* Price + CTA */}
                <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between">
                    <div>
                        <p className="text-xs text-gray-400">Giá thuê / ngày</p>
                        <p className="text-lg font-bold text-blue-600">
                            {laptop.price.toLocaleString("vi-VN")}
                            <span className="text-sm font-normal text-gray-500"> ₫</span>
                        </p>
                    </div>
                    <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors">
                        Thuê ngay
                    </button>
                </div>
            </div>
        </div>
    );
}

function Pagination({
    page,
    totalPages,
    onPageChange,
}: {
    page: number;
    totalPages: number;
    onPageChange: (p: number) => void;
}) {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const delta = 2;
        const range: number[] = [];
        for (let i = Math.max(1, page - delta); i <= Math.min(totalPages, page + delta); i++) {
            range.push(i);
        }
        return range;
    };

    return (
        <div className="flex items-center justify-center gap-1 mt-8">
            <button disabled={page === 1} onClick={() => onPageChange(1)} className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition">«</button>
            <button disabled={page === 1} onClick={() => onPageChange(page - 1)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition">‹</button>
            {getPageNumbers().map((p) => (
                <button key={p} onClick={() => onPageChange(p)} className={`px-3 py-1.5 text-sm rounded-lg border transition ${p === page ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 hover:bg-gray-50"}`}>{p}</button>
            ))}
            <button disabled={page === totalPages} onClick={() => onPageChange(page + 1)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition">›</button>
            <button disabled={page === totalPages} onClick={() => onPageChange(totalPages)} className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition">»</button>
        </div>
    );
}

// ── Main ─────────────────────────────────────────────────────────────────────
const LIMIT = 12;

export default function LaptopRental() {
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState("");

    const { data, isLoading, isError } = useGetAllLaptop({
        page,
        limit: LIMIT,
        status: status || undefined,
    });

    const laptops: Laptop[] = data?.items ?? [];
    const meta: Meta | undefined = data?.meta;
    const totalItems = meta?.totalItems ?? 0;
    const totalPages = meta?.totalPages ?? 0;

    const handleStatusChange = (value: string) => {
        setStatus(value);
        setPage(1);
    };

    return (
        <Layout>
            <div className="p-6 max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Dịch vụ cho thuê laptop</h1>
                    <p className="text-gray-500 mt-1">Chọn laptop phù hợp với nhu cầu của bạn</p>
                </div>

                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                    <div className="flex items-center gap-2">
                        {STATUS_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                onClick={() => handleStatusChange(opt.value)}
                                className={`px-4 py-1.5 text-sm rounded-full border font-medium transition ${
                                    status === opt.value
                                        ? "bg-blue-600 text-white border-blue-600"
                                        : "border-gray-300 text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <span className="text-sm text-gray-500">
                        <strong className="text-gray-800">{totalItems}</strong> sản phẩm
                    </span>
                </div>

                {/* Grid */}
                {isError ? (
                    <div className="py-20 text-center text-red-500">
                        Đã xảy ra lỗi khi tải dữ liệu. Vui lòng thử lại.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {isLoading
                            ? Array.from({ length: LIMIT }).map((_, i) => <CardSkeleton key={i} />)
                            : laptops.length === 0
                            ? (
                                <div className="col-span-full py-20 text-center text-gray-400">
                                    Hiện chưa có laptop nào
                                </div>
                            )
                            : laptops.map((laptop, idx) => (
                                <LaptopCard
                                    key={laptop.id}
                                    laptop={laptop}
                                    idx={idx}
                                    page={page}
                                    limit={LIMIT}
                                />
                            ))}
                    </div>
                )}

                <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
        </Layout>
    );
}